import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ChevronLeft, MapPin, Search } from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AddAddressPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef(null);

  const [formData, setFormData] = useState({
    label: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    postal_code: '',
    country: 'UK',
    latitude: 0,
    longitude: 0,
    is_default: false
  });

  // Search for addresses using postcode or address
  const searchAddress = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/addresses/geocode`, null, {
        params: { address: query }
      });

      if (response.data) {
        // Parse the formatted address
        const formatted = response.data.formatted_address;
        const parts = formatted.split(', ');
        
        setSuggestions([{
          formatted_address: formatted,
          latitude: response.data.latitude,
          longitude: response.data.longitude
        }]);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  // Handle search input change with debounce
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for search
    if (value.length >= 3) {
      searchTimeoutRef.current = setTimeout(() => {
        searchAddress(value);
      }, 500);
    } else {
      setSuggestions([]);
    }
  };

  // Select address from suggestions
  const selectAddress = (suggestion) => {
    const parts = suggestion.formatted_address.split(', ');
    
    setFormData({
      ...formData,
      address_line_1: parts[0] || '',
      city: parts[parts.length - 3] || '',
      postal_code: parts[parts.length - 2] || '',
      country: parts[parts.length - 1] || 'UK',
      latitude: suggestion.latitude,
      longitude: suggestion.longitude
    });

    setSearchQuery(suggestion.formatted_address);
    setShowSuggestions(false);
    toast.success('Address verified!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.latitude === 0 || formData.longitude === 0) {
      toast.error('Please search and select an address first');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/addresses`, formData);
      toast.success('Address saved!');
      navigate(-1);
    } catch (error) {
      toast.error('Failed to save address');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-neutral-600 hover:text-neutral-900">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Add Your Location</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-neutral-200 p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <MapPin className="w-6 h-6 text-[#007AFF]" />
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Where are you located?
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Address Search */}
            <div className="relative">
              <Label htmlFor="search">Search by Postcode or Address</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <Input
                  id="search"
                  type="text"
                  placeholder="e.g., SW1A 2AA or 10 Downing Street, London"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="h-12 pl-10"
                  autoComplete="off"
                />
              </div>

              {/* Suggestions dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-2 bg-white border border-neutral-200 rounded-lg shadow-lg">
                  {suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => selectAddress(suggestion)}
                      className="w-full px-4 py-3 text-left hover:bg-neutral-50 transition-colors border-b border-neutral-100 last:border-0"
                    >
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-[#007AFF] mt-1 flex-shrink-0" />
                        <span className="text-sm">{suggestion.formatted_address}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <p className="text-sm text-neutral-600 mt-2">
                Start typing to search for your address
              </p>
            </div>

            {/* Verified Address Display */}
            {formData.latitude !== 0 && formData.longitude !== 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 font-semibold flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4" />
                  Address Verified
                </p>
                <p className="text-sm text-green-700">
                  {formData.address_line_1}, {formData.city}, {formData.postal_code}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Coordinates: {formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)}
                </p>
              </div>
            )}

            <div className="border-t border-neutral-200 pt-6">
              <Label htmlFor="label">Save this location as</Label>
              <Input
                id="label"
                data-testid="label-input"
                type="text"
                placeholder="e.g., Home, Office, Workshop"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                required
                className="h-12"
              />
            </div>

            <div>
              <Label htmlFor="address_line_2">Apartment, Suite, Floor (Optional)</Label>
              <Input
                id="address_line_2"
                data-testid="address-line-2-input"
                type="text"
                placeholder="Flat 2B, Floor 3"
                value={formData.address_line_2}
                onChange={(e) => setFormData({ ...formData, address_line_2: e.target.value })}
                className="h-12"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default"
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="is_default" className="cursor-pointer">
                Set as default location
              </Label>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex-1"
              >
                Cancel
              </Button>
              
              <Button
                type="submit"
                data-testid="save-address-btn"
                disabled={loading || formData.latitude === 0}
                className="flex-1 bg-[#007AFF] hover:bg-[#0066CC] text-white rounded-full"
              >
                {loading ? 'Saving...' : 'Save Location'}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default AddAddressPage;
